// ============================================
// ConverseTech Frontend - Session Detail Page
// ============================================

import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Check, X, AlertCircle, MessageSquare, Shield, Layout } from 'lucide-react';
import { getSession, getSessionMistakes } from '../lib/api';
import type { Mistake, MistakeType } from '@conversetech/shared';
import { TRAINING_MODE_LABELS } from '@conversetech/shared';

export default function SessionDetail() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['session', id],
    queryFn: () => getSession(id!),
    enabled: !!id,
  });

  const { data: mistakesData } = useQuery({
    queryKey: ['session-mistakes', id],
    queryFn: () => getSessionMistakes(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !data?.session) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Session not found</p>
          <Link to="/dashboard" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const session = data.session;
  const metrics = session.metrics;
  const mistakes = mistakesData?.mistakes || [];

  return (
    <div className="min-h-screen bg-gray-50 safe-area-inset-y">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 safe-area-inset-top">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <Link to="/dashboard" className="flex items-center gap-2 text-gray-500 hover:text-gray-700 p-1 -m-1">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm sm:text-base">Back to Dashboard</span>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 sm:py-8 safe-area-inset-x">
        {/* Session Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            {TRAINING_MODE_LABELS[session.mode]} Session
          </h1>
          <p className="text-sm sm:text-base text-gray-500">
            {new Date(session.started_at).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Metrics */}
          <div data-testid="session-metrics" className="space-y-4 sm:space-y-6">
            {/* DET Score */}
            {metrics && (
              <div className="card p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Overall Score</h2>
                <div data-testid="metric-det-score" className="text-center">
                  <span data-testid="metric-det-score-value" className="text-4xl sm:text-5xl font-bold text-primary-600">
                    {metrics.estimated_det_score}
                  </span>
                  <p className="text-sm sm:text-base text-gray-500 mt-1">Estimated DET Score</p>
                </div>
              </div>
            )}

            {/* Fluency Metrics */}
            {metrics && (
              <div className="card p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Fluency</h2>
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <MetricItem
                    testId="metric-start-latency"
                    valueTestId="metric-start-latency-value"
                    label="Start Latency"
                    value={`${metrics.fluency.start_latency}`}
                    unit="s"
                  />
                  <MetricItem
                    testId="metric-speech-rate"
                    valueTestId="metric-speech-rate-value"
                    label="Speech Rate"
                    value={`${metrics.fluency.speech_rate_wpm}`}
                    unit="WPM"
                  />
                  <MetricItem
                    testId="metric-pause-rate"
                    valueTestId="metric-pause-rate-value"
                    label="Pause Rate"
                    value={`${metrics.fluency.pause_rate}`}
                    unit="/min"
                  />
                </div>
              </div>
            )}

            {/* Structure Score */}
            {metrics && (
              <div className="card p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Structure</h2>
                <div data-testid="metric-structure-score" className="mb-3 sm:mb-4">
                  <span className="text-xs sm:text-sm text-gray-500">Score: </span>
                  <span data-testid="metric-structure-score-value" className="text-lg sm:text-xl font-bold">
                    {metrics.structure.score}
                  </span>
                  <span className="text-gray-500">/100</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <StructureItem
                    testId="structure-has-intro"
                    label="Context"
                    present={metrics.structure.has_intro}
                  />
                  <StructureItem
                    testId="structure-has-problem"
                    label="Problem"
                    present={metrics.structure.has_problem}
                  />
                  <StructureItem
                    testId="structure-has-solution"
                    label="Solution"
                    present={metrics.structure.has_solution}
                  />
                  <StructureItem
                    testId="structure-has-tradeoffs"
                    label="Trade-offs"
                    present={metrics.structure.has_tradeoffs}
                  />
                </div>
              </div>
            )}

            {/* Confidence Index */}
            {metrics && (
              <div className="card p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Confidence</h2>
                <div data-testid="metric-confidence-index" className="mb-3 sm:mb-4">
                  <span className="text-xs sm:text-sm text-gray-500">Index: </span>
                  <span data-testid="metric-confidence-index-value" className="text-lg sm:text-xl font-bold">
                    {metrics.confidence.index}
                  </span>
                  <span className="text-gray-500">/100</span>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <MetricItem
                    testId="metric-filler-count"
                    valueTestId="metric-filler-count-value"
                    label="Filler Words"
                    value={`${metrics.confidence.filler_count}`}
                  />
                  <MetricItem
                    testId="metric-hedging-count"
                    valueTestId="metric-hedging-count-value"
                    label="Hedging Phrases"
                    value={`${metrics.confidence.hedging_count}`}
                  />
                </div>
              </div>
            )}

            {/* Pressure Metrics */}
            {metrics && (
              <div className="card p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Under Pressure</h2>
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <MetricItem
                    testId="metric-initial-score"
                    valueTestId="metric-initial-score-value"
                    label="Initial"
                    value={`${metrics.pressure.initial_score}`}
                  />
                  <MetricItem
                    testId="metric-final-score"
                    valueTestId="metric-final-score-value"
                    label="Final"
                    value={`${metrics.pressure.final_score}`}
                  />
                  <MetricItem
                    testId="metric-pressure-drop"
                    valueTestId="metric-pressure-drop-value"
                    label="Drop"
                    value={`${metrics.pressure.drop_percentage}`}
                    unit="%"
                  />
                </div>
              </div>
            )}

            {/* Vocabulary Metrics */}
            {metrics?.vocabulary && (
              <div className="card p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Vocabulary</h2>
                <div data-testid="metric-vocabulary-score" className="mb-3 sm:mb-4">
                  <span className="text-xs sm:text-sm text-gray-500">Score: </span>
                  <span data-testid="metric-vocabulary-score-value" className="text-lg sm:text-xl font-bold">
                    {metrics.vocabulary.score}
                  </span>
                  <span className="text-gray-500">/100</span>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <MetricItem
                    testId="metric-ttr"
                    valueTestId="metric-ttr-value"
                    label="Lexical Diversity"
                    value={`${metrics.vocabulary.type_token_ratio}`}
                  />
                  <MetricItem
                    testId="metric-total-words"
                    valueTestId="metric-total-words-value"
                    label="Total Words"
                    value={`${metrics.vocabulary.total_words}`}
                  />
                  <MetricItem
                    testId="metric-advanced-words"
                    valueTestId="metric-advanced-words-value"
                    label="Advanced Words"
                    value={`${metrics.vocabulary.advanced_word_count}`}
                  />
                  <MetricItem
                    testId="metric-academic-words"
                    valueTestId="metric-academic-words-value"
                    label="Academic Words"
                    value={`${metrics.vocabulary.academic_word_count}`}
                  />
                </div>
              </div>
            )}

            {/* Mistakes */}
            {mistakes.length > 0 && (
              <div data-testid="session-mistakes" className="card p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                  Areas for Improvement ({mistakes.length})
                </h2>
                <div className="space-y-2 sm:space-y-3">
                  {mistakes.map((mistake) => (
                    <MistakeItem key={mistake.id} mistake={mistake} />
                  ))}
                </div>
              </div>
            )}

            {/* Breakdown */}
            <div data-testid="metrics-breakdown" className="card p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Score Breakdown</h2>
              <p className="text-xs sm:text-sm text-gray-500">
                DET Score = (30% Fluency + 30% Structure + 15% Confidence + 10% Pressure + 15% Vocabulary) x 150 + 10
              </p>
            </div>
          </div>

          {/* Transcript */}
          <div data-testid="session-transcript" className="card p-4 sm:p-6 h-fit max-h-[500px] sm:max-h-[800px] overflow-y-auto">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Transcript</h2>
            {session.transcript ? (
              <div className="space-y-3 text-sm">
                {session.transcript.split('\n').map((line, i) => {
                  const isUser = line.startsWith('[user]');
                  const text = line.replace(/^\[(user|ai)\]\s*/, '');
                  return (
                    <div
                      key={i}
                      className={`p-3 rounded-lg ${
                        isUser ? 'bg-primary-50 text-primary-900' : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <span className="text-xs text-gray-500 block mb-1">
                        {isUser ? 'You' : 'AI Coach'}
                      </span>
                      {text}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No transcript available</p>
            )}
          </div>
        </div>

        {/* Recording Player */}
        {session.recording_url && (
          <div data-testid="recording-player" className="mt-6 sm:mt-8 card p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Recording</h2>
            <audio controls className="w-full">
              <source src={session.recording_url} type="audio/webm" />
              Your browser does not support the audio element.
            </audio>
          </div>
        )}
      </main>
    </div>
  );
}

function MetricItem({
  testId,
  valueTestId,
  label,
  value,
  unit,
}: {
  testId: string;
  valueTestId: string;
  label: string;
  value: string;
  unit?: string;
}) {
  return (
    <div data-testid={testId} className="text-center">
      <div className="text-xs sm:text-sm text-gray-500 truncate">{label}</div>
      <div>
        <span data-testid={valueTestId} className="text-base sm:text-lg font-semibold">
          {value}
        </span>
        {unit && <span className="text-xs sm:text-sm text-gray-500 ml-0.5 sm:ml-1">{unit}</span>}
      </div>
    </div>
  );
}

function StructureItem({
  testId,
  label,
  present,
}: {
  testId: string;
  label: string;
  present: boolean;
}) {
  return (
    <div data-testid={testId} className="flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 bg-gray-50 rounded">
      {present ? (
        <span data-testid="check-icon" className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
          <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
        </span>
      ) : (
        <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-gray-200 text-gray-400 flex items-center justify-center flex-shrink-0">
          <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
        </span>
      )}
      <span className={`text-xs sm:text-sm ${present ? 'text-gray-900' : 'text-gray-400'}`}>{label}</span>
    </div>
  );
}

const MISTAKE_TYPE_CONFIG: Record<MistakeType, {
  icon: React.ReactNode;
  label: string;
  color: string;
  bgColor: string;
}> = {
  grammar: {
    icon: <AlertCircle className="w-4 h-4" />,
    label: 'Grammar',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
  },
  filler: {
    icon: <MessageSquare className="w-4 h-4" />,
    label: 'Filler Word',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
  },
  hedging: {
    icon: <Shield className="w-4 h-4" />,
    label: 'Hedging',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
  },
  structure: {
    icon: <Layout className="w-4 h-4" />,
    label: 'Structure',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
};

function MistakeItem({ mistake }: { mistake: Mistake }) {
  const config = MISTAKE_TYPE_CONFIG[mistake.mistake_type];

  return (
    <div
      data-testid={`mistake-${mistake.id}`}
      className={`p-2.5 sm:p-3 rounded-lg ${config.bgColor} border border-gray-200`}
    >
      <div className="flex items-start gap-2">
        <span className={`mt-0.5 ${config.color} flex-shrink-0`}>
          <span className="w-4 h-4 sm:w-5 sm:h-5">{config.icon}</span>
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
            <span className={`text-[10px] sm:text-xs font-medium ${config.color}`}>
              {config.label}
            </span>
            <span className="text-xs sm:text-sm font-medium text-gray-900">
              "{mistake.snippet}"
            </span>
          </div>
          {mistake.suggested_fix && (
            <p className="text-xs sm:text-sm text-gray-600">{mistake.suggested_fix}</p>
          )}
          {mistake.context && (
            <p className="text-[10px] sm:text-xs text-gray-400 mt-1 truncate">
              Context: ...{mistake.context}...
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
